import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Grid, Paper, Chip, Tooltip } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import { useLocation } from "react-router-dom";
import QuizRepository from "../../../core/repository/QuizRepository";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import { getSourcePath } from "../../../core/Constant";
import WaterMark from "@uiw/react-watermark";
import UserRepository from "../../../core/repository/UserRepository";
import { useWatermarkProtection } from "../../../hook/useWatermarkProtection";

const QuizReviewAnswersScreen = () => {
  const location = useLocation();
  const { quizId, questionIds, attempt } = location.state || {};
  const [quizData, setQuizData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [watermarkKey, setWatermarkKey] = useState(0);
  const QUESTIONS_PER_PAGE = 2;
  const hasTriggeredRef = useRef(false);
       
   const sendTamperingAlert = async () => {
           try {
             const res = await UserRepository.temperingAlertMail();
           } catch (e) {
             console.error("Tampering alert error:", e);
           }
         };
     
  useWatermarkProtection({
  onTamper: () => {
           if (!hasTriggeredRef.current) {
           hasTriggeredRef.current = true;
           sendTamperingAlert(); 
         }
         setWatermarkKey(prev => prev + 1);
         }
       });      
  
  const [userInfo, setUserInfo] = useState({
    name: "",
    contact: "",
    email: "",
  });
  useEffect(() => {
    const getUser = async () => {
      const user = await UserRepository.getUserDetail();
      setUserInfo({
        name: `${user?.data?.first_name || ""} ${user?.data?.last_name || ""}`.trim(),
        contact: user?.data?.phone_number || "",
        email: user?.data?.email || "",
      });
    };
    getUser();
  }, []);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        let paramString = quizId;
        const queryParts = [];
        if (
          questionIds &&
          Array.isArray(questionIds) &&
          questionIds.length > 0
        ) {
          queryParts.push(`question_ids=${questionIds.join(",")}`);
        }
        if (attempt != null) {
          queryParts.push(
            `attempt=${encodeURIComponent(String(attempt))}`
          );
        }
        if (queryParts.length > 0) {
          paramString += `?${queryParts.join("&")}`;
        }
        const data = await QuizRepository.stdgetQuestion(paramString);

        setQuizData(data.data || []);
      } catch (e) {
        setQuizData([]);
      }
      setLoading(false);
    };
    if (quizId) fetchQuestions();
  }, [quizId, questionIds, attempt]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  // Pagination logic
  const totalPages = Math.ceil(quizData.length / QUESTIONS_PER_PAGE);
  const startIdx = page * QUESTIONS_PER_PAGE;
  const endIdx = startIdx + QUESTIONS_PER_PAGE;
  const currentQuestions = quizData.slice(startIdx, endIdx);

  const isQuestionFlagged = (item) => {
    const v = item?.is_flag === 1;
    return v
  };

  return (
    <Box className="main-screen-container">
      <Box mt={1} mb={2}>
        <HeadingComponent
          text={"Back to Quiz"}
          backNavigation={true}
          fontweigth={600}
          size={26}
          fontfamily={"Montserrat"}
        />
      </Box>
      <Grid
        container
        rowSpacing={3}
        columnSpacing={{ xs: 0, sm: 3 }}
        alignItems="stretch"
      >
        {currentQuestions.map((q, idx) => (
          <Grid item xs={12} md={6} key={q.id}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
              }}
            >
              <WaterMark  key={watermarkKey} className="watermark-protection"
                content={[
                  userInfo.name,
                  userInfo.contact,
                  userInfo.email,
                ]}
                gap={[260, 260]}
                fontSize={12}
                zIndex={10}
                rotate={-20}
                fontColor="rgba(0, 0, 0, 0.25)"

              >
                {/* Question Image */}
              {(q?.image || (q?.images && q.images.length > 0)) && (
                <Box my={2} display="flex" justifyContent="center" width="100%">
                  {q?.images && q.images.length > 0 ? (
                    <Grid container spacing={1} justifyContent="center">
                      {q.images.map((img, index) => (
                        <Grid item key={img.id || index}>
                          <Box
                            component="img"
                            src={getSourcePath(img.image_path)} // Extracting path from object
                            alt={`Question-visual-${index}`}
                            sx={{
                              width: "100%",
                              maxWidth: q.images.length === 1 ? 400 : 180, // Larger if only one
                              maxHeight: 200,
                              borderRadius: 2,
                              objectFit: "contain",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              backgroundColor: "#f9f9f9",
                              border: "1px solid #eee"
                            }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box
                      component="img"
                      src={getSourcePath(q.image)}
                      alt="Question"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 160,
                        borderRadius: 2,
                        objectFit: "contain",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                </Box>
              )}
                <Box mb={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.75}
                    mb={0.5}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      component="span"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      Q{startIdx + idx + 1}.
                    </Typography>
                    {isQuestionFlagged(q) ? (
                      <Tooltip title="Flagged for review" arrow>
                        <FlagIcon
                          sx={{ color: "#FFB200", flexShrink: 0 }}
                          fontSize="medium"
                          aria-label="Flagged for review"
                        />
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                    <span dangerouslySetInnerHTML={{ __html: q.question }} />
                  </Typography>
                </Box>

                <Box mb={2}>
                  {q.answers?.map((ans) => {
                    // Map ans.id to letter: 1->A, 2->B, ...
                    const labelLetter = String.fromCharCode(64 + Number(ans.id));
                    return (
                      <Tooltip title={ans.answer} key={ans.id} arrow>
                        <Chip
                          label={`${labelLetter}. ${ans.answer}`}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>

                <Typography
                  variant="subtitle2"
                  color="#28b882"
                  fontWeight={700}
                  mb={1}
                >
                  Correct Answer:
                </Typography>

                <Box mb={2}>
                  {q.answers
                    ?.filter((a) => a.is_correct === 1)
                    .map((a) => {
                      const labelLetter = String.fromCharCode(64 + Number(a.id));
                      return (
                        <Tooltip title={a.answer} key={a.id} arrow>
                          <Chip
                            label={`${labelLetter}. ${a.answer}`}
                            color="success"
                            sx={{ fontWeight: 700 }}
                          />
                        </Tooltip>
                      );
                    })}
                </Box>

                  <Box mt={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#9834F0"
                    >
                      Reason:
                    </Typography>
                    <Box
                      sx={{
                        '& img': {
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          marginTop: 1,
                          marginBottom: 1,
                          borderRadius: 1,
                        }
                      }}
                    >
                      <Typography variant="body2">
                      {q.reason !== null ? (
                        <span dangerouslySetInnerHTML={{ __html: q.reason }} />
                      ) : (
                        <span>Reason not found</span>
                      )}
                      </Typography>
                    </Box>
                  </Box>
                <Box flexGrow={1} />
              </WaterMark>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Pagination controls */}
      <Box mt={4} textAlign="center">
        <Chip
          label="Previous"
          color="primary"
          clickable={page > 0}
          onClick={() => setPage(page - 1)}
          sx={{
            mr: 2,
            opacity: page > 0 ? 1 : 0.5,
            pointerEvents: page > 0 ? "auto" : "none",
          }}
        />
        <Chip
          label="Next"
          color="primary"
          clickable={page < totalPages - 1}
          onClick={() => setPage(page + 1)}
          sx={{
            opacity: page < totalPages - 1 ? 1 : 0.5,
            pointerEvents: page < totalPages - 1 ? "auto" : "none",
          }}
        />
      </Box>
    </Box>
  );
};

export default QuizReviewAnswersScreen;
